import { z } from "zod";
import { AiCall } from "./schemas";

export const AiCallDimension = z
  .enum([
    "lab",
    "model",
    "router",
    "transport",
    "host",
    "sku",
    "agent",
    "agentVersion",
    "payer",
    "gameKind",
    "purpose",
    "serviceTier",
    "inferenceLocation",
    "fault",
    "errorType",
    "outcome",
  ])
  .meta({
    id: "AiCallDimension",
    description: [
      "Something calls can be split or filtered by.",
      "",
      "- `lab`: who trained the model, such as `anthropic` or `moonshotai`.",
      "- `router`: `direct`, or the router that sent it, such as `openrouter`.",
      "- `transport`: the vendor endpoint the call came over, such as `openrouter-chat-completions`.",
      "- `host`: who served it. For a direct call that's the vendor itself.",
      "- `sku`: the SKU the call was priced at, such as `alibaba:qwen3.8-max:virginia:standard`.",
      "- `serviceTier`: the tier the response reported, else the one asked for.",
      "- `inferenceLocation`: where the response said the model ran, else where it was asked to.",
      "- `payer`: a player the call was charged to. A shared call counts once for each of its payers.",
      "- `fault`: `provider`, `request`, `model`, `cancelled`, or `none`.",
      "- `errorType`: the call's error type, or its output error's, or `none`.",
      "- `outcome`: `succeeded`, `malformed` or `failed`.",
    ].join("\n"),
    example: "model",
  });
export type AiCallDimension = z.infer<typeof AiCallDimension>;

export const AiCallFilter = z
  .strictObject({
    from: z.iso.datetime().meta({ description: "Calls started at or after this." }),
    to: z.iso.datetime().meta({ description: "Calls started before this." }),
    keep: z
      .partialRecord(AiCallDimension, z.array(z.string()).min(1).max(50))
      .default({})
      .meta({ description: "For each dimension, the values a call must have one of." }),
    promptTokensOver: z.number().int().nonnegative().optional().meta({
      description: "Only calls whose prompt, cached tokens included, was longer than this.",
    }),
  })
  .meta({
    id: "AiCallFilter",
    description: "Which calls a question is about.",
    example: {
      from: "2026-09-25T00:00:00.000Z",
      to: "2026-10-02T00:00:00.000Z",
      keep: { model: ["gpt-6-luna"] },
    },
  });
export type AiCallFilter = z.infer<typeof AiCallFilter>;

const TokenSplit = z.object({
  uncachedInput: z.number(),
  cacheRead: z.number(),
  cacheWrite: z.number(),
  reasoning: z.number(),
  output: z.number(),
});

const SpendSplit = TokenSplit.extend({ tools: z.number() });

export const AiCallTotals = z
  .object({
    calls: z.number().int(),
    failed: z.number().int(),
    malformed: z.number().int(),
    toolCalls: z.number().int(),
    tokens: TokenSplit.meta({ description: "Tokens of each kind. `output` excludes reasoning." }),
    costUsd: z.number().meta({
      description:
        "What the calls cost, in US dollars: each call's billed cost, or its list cost when nothing billed it. Calls whose cost is unknown add nothing.",
    }),
    spendUsd: SpendSplit.meta({
      description:
        "The list cost of every priced line, by kind: tokens, then `tools` for tools and requests billed per use. Reasoning is billed as output, so its share is the output cost of its tokens. A call billed apart from its list price, such as through a router, makes these differ from `costUsd`.",
    }),
    unknownCostCalls: z.number().int().meta({
      description: "Calls whose response reported no usage, so what they cost is unknown.",
    }),
    unpricedCalls: z.number().int().meta({
      description: "Calls with usage Pax has no price for: a gap in Pax's price data.",
    }),
    firstTokenP50Ms: z.number().nullable(),
    firstTokenP95Ms: z.number().nullable(),
    outputTokensPerSecond: z.number().nullable().meta({
      description: "Output tokens per second from the first streamed event to the last.",
    }),
    errorTypes: z.record(z.string(), z.number().int()).meta({
      description: "Failed and malformed calls by error type.",
    }),
    faults: z.record(z.string(), z.number().int()).meta({
      description: "Failed and malformed calls by fault.",
    }),
  })
  .meta({ id: "AiCallTotals", description: "What a set of calls adds up to." });
export type AiCallTotals = z.infer<typeof AiCallTotals>;

export const AnalyzeAiCallsRequest = z
  .strictObject({
    filter: AiCallFilter,
    groupBy: AiCallDimension,
    bucketSeconds: z.number().int().min(60).max(86_400).optional().meta({
      description: "The width of each point in `series`. Chosen from the range when left out.",
    }),
  })
  .meta({ id: "AnalyzeAiCallsRequest", description: "One question about calls." });
export type AnalyzeAiCallsRequest = z.infer<typeof AnalyzeAiCallsRequest>;

export const AnalyzeAiCallsResponse = z
  .object({
    totals: AiCallTotals,
    groups: z
      .array(z.object({ value: z.string(), totals: AiCallTotals }))
      .meta({ description: "One per value of `groupBy`, most calls first, at most 50." }),
    bucketSeconds: z.number().int(),
    series: z
      .array(z.object({ start: z.iso.datetime(), value: z.string(), totals: AiCallTotals }))
      .meta({ description: "Each group's totals in each bucket that has calls, oldest first." }),
    totalSeries: z
      .array(z.object({ start: z.iso.datetime(), totals: AiCallTotals }))
      .meta({ description: "Every call's totals in each bucket that has calls, oldest first." }),
    names: z
      .record(z.string(), z.string())
      .meta({ description: "Display names for values that are ids, such as agents." }),
  })
  .meta({ id: "AnalyzeAiCallsResponse", description: "The answer to one question about calls." });
export type AnalyzeAiCallsResponse = z.infer<typeof AnalyzeAiCallsResponse>;

export const AiCallOrder = z
  .enum(["latest", "expensive", "slowest"])
  .meta({ id: "AiCallOrder", description: "How matching calls are ordered." });
export type AiCallOrder = z.infer<typeof AiCallOrder>;

export const SearchAiCallsRequest = z
  .strictObject({
    filter: AiCallFilter,
    order: AiCallOrder.default("latest"),
    limit: z.number().int().min(1).max(100).default(20),
    cursor: z.string().optional().meta({ description: "`nextCursor` from the previous page." }),
  })
  .meta({ id: "SearchAiCallsRequest", description: "Which calls to list, and in what order." });
export type SearchAiCallsRequest = z.infer<typeof SearchAiCallsRequest>;

export const SearchAiCallsResponse = z
  .object({
    items: z.array(AiCall),
    nextCursor: z.string().nullable(),
    total: z.object({
      count: z.number().int(),
      exact: z.boolean().meta({ description: "False when `count` is an estimate." }),
    }),
    names: z
      .record(z.string(), z.string())
      .meta({ description: "`name vN` for the agent version of each call that has one." }),
  })
  .meta({ id: "SearchAiCallsResponse", description: "One page of matching calls." });
export type SearchAiCallsResponse = z.infer<typeof SearchAiCallsResponse>;
